import { BUILTIN_SCENES, type BuiltinScene } from '@numslearn/shared';

/**
 * The picture a puzzle board hides.
 *
 * It is never drawn *behind* the board. That was the first shape of this, and
 * it gave the game away: the gaps between the tiles showed enough of the scene
 * to name it, and on Paper — whose tiles are transparent by design — the whole
 * picture simply sat there in plain sight. A child could say "it's the rocket"
 * without solving a single pair.
 *
 * So each tile carries its own window onto the picture, and a window is only
 * mounted once its tile has been cleared. Until then the image is not on the
 * screen at all, and every piece appears in the place its tile occupied.
 *
 * The built-in scenes are inline SVG serialised to a data URI rather than
 * shipped as files: nothing to download, they scale to any board size, and
 * they travel the same `<img>` path as a teacher's uploaded photo, so there is
 * one reveal to get right instead of two.
 */

/** The scene names live in the shared package, so the server can draw one too. */
type SceneName = BuiltinScene;

/**
 * One cell's window onto the picture.
 *
 * The `<img>` inside is the size of the *whole* board and is pushed up and
 * back by this cell's offset, so the piece shows exactly the part of the
 * picture the tile was covering — gaps included, which is why a solved board
 * reads as one photograph behind a grid rather than a set of squeezed
 * thumbnails. The arithmetic lives in the stylesheet; all it needs from here
 * is which cell this is.
 */
export function PuzzlePiece({ src, col, row }: { src: string; col: number; row: number }) {
  return (
    <span
      className="puz-piece"
      style={{ '--pz-col': String(col), '--pz-row': String(row) } as React.CSSProperties}
      aria-hidden="true"
    >
      <img src={src} alt="" draggable={false} />
    </span>
  );
}

const svgUrl = (body: string) =>
  `data:image/svg+xml,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid slice">${body}</svg>`,
  )}`;

const PAINT: Record<SceneName, string> = {
  rocket: `
    <rect width="100" height="100" fill="#0d1b3e"/>
    <g fill="#fff9d8">
      <circle cx="12" cy="16" r="1.4"/><circle cx="86" cy="12" r="1.1"/>
      <circle cx="72" cy="30" r="1"/><circle cx="22" cy="72" r="1.2"/>
      <circle cx="90" cy="66" r="1.3"/><circle cx="46" cy="9" r="1"/>
    </g>
    <ellipse cx="50" cy="93" rx="34" ry="9" fill="#2b3f76"/>
    <path d="M50 14C64 30 68 48 66 64H34C32 48 36 30 50 14Z" fill="#f2f4fa"/>
    <path d="M34 58C22 62 18 74 20 82l14-10Z" fill="#e2483b"/>
    <path d="M66 58c12 4 16 16 14 24l-14-10Z" fill="#e2483b"/>
    <circle cx="50" cy="42" r="10" fill="#7fd3f0" stroke="#2b3f76" stroke-width="2.6"/>
    <path d="M40 64h20l-4 10H44Z" fill="#c8cfe0"/>
    <path d="M50 74c6 6 6 14 0 22-6-8-6-16 0-22Z" fill="#ffb43f"/>
    <path d="M50 78c3 4 3 9 0 14-3-5-3-10 0-14Z" fill="#ffe27a"/>
  `,
  cat: `
    <rect width="100" height="100" fill="#ffe6c7"/>
    <circle cx="50" cy="82" r="30" fill="#f2a03d"/>
    <path d="M26 40 22 16l20 12Z" fill="#f2a03d"/>
    <path d="M74 40 78 16 58 28Z" fill="#f2a03d"/>
    <path d="M28 38 26 24l12 7Z" fill="#ffc7a1"/>
    <path d="M72 38 74 24l-12 7Z" fill="#ffc7a1"/>
    <circle cx="50" cy="48" r="26" fill="#ffb85c"/>
    <ellipse cx="40" cy="45" rx="4.4" ry="6" fill="#31241a"/>
    <ellipse cx="60" cy="45" rx="4.4" ry="6" fill="#31241a"/>
    <circle cx="41.6" cy="43" r="1.6" fill="#fff"/><circle cx="61.6" cy="43" r="1.6" fill="#fff"/>
    <path d="M50 55l-4 3h8Z" fill="#e2645c"/>
    <path d="M50 58c0 4-4 5-7 3M50 58c0 4 4 5 7 3" stroke="#31241a" stroke-width="2" fill="none" stroke-linecap="round"/>
    <g stroke="#31241a" stroke-width="1.6" stroke-linecap="round">
      <path d="M30 50H14"/><path d="M30 55H16"/><path d="M70 50h16"/><path d="M70 55h14"/>
    </g>
  `,
  boat: `
    <rect width="100" height="62" fill="#8fd8f2"/>
    <circle cx="78" cy="18" r="11" fill="#ffd75e"/>
    <rect y="62" width="100" height="38" fill="#2a86b8"/>
    <path d="M0 62q12-6 24 0t24 0 24 0 28 0v6H0Z" fill="#3aa0d0"/>
    <path d="M50 14v48" stroke="#7a5230" stroke-width="3.4"/>
    <path d="M52 18l30 38H52Z" fill="#f4f6fb"/>
    <path d="M48 24 22 56h26Z" fill="#e2483b"/>
    <path d="M18 62h64l-10 16H28Z" fill="#7a5230"/>
    <path d="M18 62h64l-2 4H20Z" fill="#9a6a40"/>
    <g fill="#fff" opacity=".85">
      <ellipse cx="20" cy="20" rx="11" ry="6"/><ellipse cx="30" cy="22" rx="8" ry="5"/>
    </g>
  `,
  tree: `
    <rect width="100" height="100" fill="#dff0d8"/>
    <circle cx="20" cy="18" r="9" fill="#ffe27a"/>
    <rect x="44" y="58" width="12" height="38" rx="3" fill="#8a5a32"/>
    <circle cx="50" cy="44" r="26" fill="#3f9e5c"/>
    <circle cx="31" cy="55" r="17" fill="#4bb36a"/>
    <circle cx="69" cy="55" r="17" fill="#4bb36a"/>
    <circle cx="50" cy="30" r="16" fill="#57c078"/>
    <g fill="#e2483b">
      <circle cx="38" cy="40" r="3.2"/><circle cx="62" cy="36" r="3.2"/>
      <circle cx="55" cy="56" r="3.2"/><circle cx="30" cy="60" r="3.2"/>
    </g>
    <path d="M0 96h100v4H0Z" fill="#8fc47a"/>
  `,
  whale: `
    <rect width="100" height="100" fill="#1e5f8c"/>
    <g fill="#bfe6f5" opacity=".5">
      <circle cx="16" cy="24" r="3"/><circle cx="26" cy="16" r="2"/><circle cx="82" cy="70" r="2.4"/>
    </g>
    <path d="M18 74c-8-16 4-32 22-32s34 10 40 20c4 6 10 8 16 6-6 10-16 12-24 10-10 8-26 10-38 6Z" fill="#4aa8d8"/>
    <path d="M20 72c-6-13 4-26 20-26s30 9 35 18c-14 10-40 14-55 8Z" fill="#7fcbe8"/>
    <circle cx="36" cy="54" r="3.4" fill="#0f3a56"/>
    <circle cx="37.2" cy="52.8" r="1.2" fill="#fff"/>
    <path d="M40 46q6-14 2-24 8 6 10 22" fill="none" stroke="#bfe6f5" stroke-width="3" stroke-linecap="round"/>
    <path d="M84 76c8 2 14-2 18-10-2 12-10 18-20 16Z" fill="#4aa8d8"/>
  `,
  balloon: `
    <rect width="100" height="100" fill="#ffeaf0"/>
    <g fill="#fff" opacity=".8">
      <ellipse cx="22" cy="24" rx="13" ry="7"/><ellipse cx="76" cy="16" rx="10" ry="6"/>
    </g>
    <path d="M50 10c16 0 26 12 26 26 0 14-14 26-26 40C38 62 24 50 24 36c0-14 10-26 26-26Z" fill="#e2483b"/>
    <path d="M50 10c6 0 9 12 9 26s-4 26-9 40c-5-14-9-26-9-40s3-26 9-26Z" fill="#ffd166"/>
    <path d="M38 62h24l-2 6H40Z" fill="#c9741f"/>
    <path d="M41 68h18l-2 12H43Z" fill="#a8622a"/>
    <g stroke="#7a5230" stroke-width="1.4"><path d="M43 68l-3-6"/><path d="M57 68l3-6"/></g>
  `,
};

/** Serialised once at module load — a data URI is stable, so it caches. */
const SCENE_URL = Object.fromEntries(
  BUILTIN_SCENES.map((name) => [name, svgUrl(PAINT[name])]),
) as Record<SceneName, string>;

export const builtinSceneUrl = (name: SceneName): string => SCENE_URL[name];

/** The built-in scenes, addressable for the teacher's picture picker. */
export const SCENE_THUMBS: ReadonlyArray<{ name: SceneName; src: string }> =
  BUILTIN_SCENES.map((name) => ({ name, src: SCENE_URL[name] }));
